import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type { Dispatch, DispatchListResponse, CreateDispatchInput, AddDispatchLineInput, ApiEnvelope } from "@/domain/types";
import axios from "axios";

const ERROR_TRANSLATIONS: Record<string, string> = {
  "Stock insuficiente": "Insufficient stock",
  "Capacidad de ubicación excedida": "Location capacity exceeded",
  "No se encontró stock para el producto en la ubicación indicada": "Stock not found for this product at the specified location",
  "Verifique que el lote coincida": "Verify the lot matches",
  "Tipo de movimiento no encontrado o inactivo": "Movement type not found or inactive",
  "Only draft dispatches can be edited or confirmed": "Only draft dispatches can be edited or confirmed",
  "Only confirmed dispatches can be cancelled": "Only confirmed dispatches can be cancelled",
  "Dispatch must have at least one detail line to be confirmed": "Dispatch must have at least one line to be confirmed",
  "Cancellation reason is required": "Cancellation reason is required",
  "Dispatch not found": "Dispatch not found",
};

function getLocale(): string {
  try { return localStorage.getItem("nexo-locale") ?? "es"; } catch { return "es"; }
}

function translateError(msg: string): string {
  if (getLocale() === "es") return msg;
  for (const [es, en] of Object.entries(ERROR_TRANSLATIONS)) {
    if (msg.includes(es)) return msg.replace(es, en);
  }
  return msg;
}

function extractMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return "Error";
  const data = err.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as Record<string, unknown>).message;
    if (typeof msg === "string") return translateError(msg);
    if (Array.isArray(msg) && msg.length > 0) return translateError(String(msg[0]));
  }
  return "Error";
}

export function isDispatchError(err: unknown): err is Error & { name: "DispatchError" } {
  return err instanceof Error && err.name === "DispatchError";
}

function throwDispatchError(err: unknown): never {
  const msg = extractMessage(err);
  const e = new Error(msg);
  e.name = "DispatchError";
  throw e;
}

export async function listDispatches(params: { page?: number; limit?: number; status?: string; warehouseId?: string; from?: string; to?: string }): Promise<DispatchListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  if (params.warehouseId) query.set("warehouseId", params.warehouseId);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  const response = await warehouseClient.get<ApiEnvelope<DispatchListResponse>>(`/dispatch?${query.toString()}`);
  return response.data.data;
}

export async function getDispatch(id: string): Promise<Dispatch> {
  const response = await warehouseClient.get<ApiEnvelope<Dispatch>>(`/dispatch/${id}`);
  return response.data.data;
}

export async function createDispatch(input: CreateDispatchInput): Promise<Dispatch> {
  try {
    const response = await warehouseClient.post<ApiEnvelope<Dispatch>>("/dispatch", input);
    return response.data.data;
  } catch (err) { throwDispatchError(err); }
}

export class DispatchLotsError extends Error {
  availableLots: string[];
  constructor(message: string, lots: string[]) {
    super(message);
    this.name = "DispatchLotsError";
    this.availableLots = lots;
  }
}

export async function addDispatchLine(dispatchId: string, input: AddDispatchLineInput): Promise<void> {
  try {
    await warehouseClient.post(`/dispatch/${dispatchId}/lines`, input);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data;
      if (data && typeof data === "object" && "data" in data) {
        const responseData = (data as any).data;
        if (responseData?.availableLots && Array.isArray(responseData.availableLots)) {
          const msg = extractMessage(err);
          throw new DispatchLotsError(msg, responseData.availableLots);
        }
      }
    }
    throwDispatchError(err);
  }
}

export async function updateDispatchLine(dispatchId: string, lineId: string, input: Partial<AddDispatchLineInput>): Promise<void> {
  try {
    await warehouseClient.patch(`/dispatch/${dispatchId}/lines/${lineId}`, input);
  } catch (err) { throwDispatchError(err); }
}

export async function deleteDispatchLine(dispatchId: string, lineId: string): Promise<void> {
  try {
    await warehouseClient.delete(`/dispatch/${dispatchId}/lines/${lineId}`);
  } catch (err) { throwDispatchError(err); }
}

export async function confirmDispatch(id: string): Promise<void> {
  try {
    await warehouseClient.post(`/dispatch/${id}/confirm`);
  } catch (err) { throwDispatchError(err); }
}

export async function cancelDispatch(id: string, reason: string): Promise<void> {
  try {
    await warehouseClient.post(`/dispatch/${id}/cancel`, { reason });
  } catch (err) { throwDispatchError(err); }
}
